import { useCallback, useMemo, useRef, useState } from "react";

import * as translator from "../../../../utils/translator";

const IMAGES_URL = process.env.REACT_APP_IMAGES_URL || '';

const STEP_IMAGES = {
    step1: `${IMAGES_URL}uploads/test-lab/test-lab-step1-box.png`,
    step2: `${IMAGES_URL}uploads/test-lab/test-lab-step2-tubes.png`,
    step3: `${IMAGES_URL}uploads/test-lab/test-lab-step3-phone.png`,
};

const STEP_META = [
    { number: 1, titleKey: "test_lab_step1_title", contentKey: "test_lab_step1_content", image: STEP_IMAGES.step1, imageAlt: "Test-Kit Verpackung", cardHeight: "h-[313px] lg:h-[290px]", imageContainerHeight: "" },
    { number: 2, titleKey: "test_lab_step2_title", contentKey: "test_lab_step2_content", image: STEP_IMAGES.step2, imageAlt: "Lanzetten für Blutprobe", cardHeight: "h-[313px] lg:h-[380px]", imageContainerHeight: "lg:h-[220px]" },
    { number: 3, titleKey: "test_lab_step3_title", contentKey: "test_lab_step3_content", image: STEP_IMAGES.step3, imageAlt: "App mit Laborergebnissen", cardHeight: "h-[313px] lg:h-[290px]", imageContainerHeight: "" },
];

interface Props {
    language?: string;
}

export default function TestLabStepsWidget({ language = "de" }: Props) {
    const t = (key: string) => translator.translate(language, "thankyou", `page/${key}`);

    const steps = useMemo(() => STEP_META.map((meta) => ({
        ...meta,
        title: t(meta.titleKey),
        description: t(meta.contentKey),
    })), [language]);

    const cardsRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleScroll = () => {
        if (!cardsRef.current) return;

        const scrollLeft = cardsRef.current.scrollLeft;
        const cardWidth = cardsRef.current.scrollWidth / STEP_META.length;
        setActiveIndex(Math.round(scrollLeft / cardWidth));
    };

    const goTo = useCallback((index: number) => {
        const grid = cardsRef.current;
        if (!grid) return;

        const cards = grid.querySelectorAll<HTMLElement>("[data-tl-step]");
        const card = cards[index];
        if (!card) return;

        const target = card.offsetLeft - (grid.clientWidth - card.clientWidth) / 2;
        grid.scrollTo({ left: target, behavior: "smooth" });
        setActiveIndex(index);
    }, []);

    const goToPrev = useCallback(() => {
        goTo(activeIndex > 0 ? activeIndex - 1 : STEP_META.length - 1);
    }, [activeIndex, goTo]);

    const goToNext = useCallback(() => {
        goTo(activeIndex < STEP_META.length - 1 ? activeIndex + 1 : 0);
    }, [activeIndex, goTo]);

    return (
        <section
            className="relative w-full overflow-hidden pt-3 md:pt-6 pb-4 md:pb-10"
            style={{ background: "linear-gradient(to bottom, white 0%, #e7f1ec 10%, #e7f1ec 90%, white 100%)" }}
        >
            {/* Decorative wavy gradient line — desktop only */}
            <img
                src={`${IMAGES_URL}uploads/test-lab/test-lab-hiw-line_desktop.svg`}
                alt=""
                aria-hidden="true"
                className="pointer-events-none select-none absolute z-0 w-full h-auto left-0 top-[20%] lg:top-[33%] hidden lg:block"
            />

            <img
                src={`${IMAGES_URL}uploads/test-lab/test-lab-hiw-line.svg`}
                alt=""
                aria-hidden="true"
                className="pointer-events-none select-none absolute z-0 w-full h-auto left-0 top-[20%] block lg:hidden"
            />

            {/* Mobile title block */}
            <div className="relative z-10 text-center mb-8 select-none lg:hidden px-4">
                <p className="font-poppins font-medium uppercase text-[#212121] text-[12px] tracking-[1.4px] leading-[3.3] m-0">
                    {t("test_lab_steps_pre_title")}
                </p>
                <h2 className="font-poppins font-medium text-[#212121] text-[32px] leading-[1.24] m-0">
                    {t("test_lab_steps_title")}
                    <br />
                    <span className="linear-text-color-primary">
                        {t("test_lab_steps_title_accent")}
                    </span>
                </h2>
            </div>

            {/* Cards container */}
            <div
                ref={cardsRef}
                onScroll={handleScroll}
                className="
                    relative
                    flex flex-row gap-[24px] overflow-x-auto pb-4 pt-6 pl-[15px] pr-[15px]
                    [scroll-snap-type:x_mandatory] [scroll-behavior:smooth]
                    [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden
                    lg:flex-none lg:grid lg:grid-cols-[1fr_1.14fr_1fr] lg:gap-[24px] lg:items-end
                    lg:justify-center lg:overflow-visible lg:px-6 lg:pt-[28px] lg:pb-[28px] lg:[scroll-snap-type:none]
                    max-w-[1100px] mx-auto
                "
            >
                {steps.map((step) => (
                    <div
                        key={step.number}
                        data-tl-step
                        className="grid grid-cols-1 grid-rows-1 flex-none [scroll-snap-align:center] w-[289px] lg:w-full"
                    >
                        {/* 1. THE CARD GOES FIRST (So it renders underneath) */}
                        <div
                            className={`
                                col-start-1 row-start-1
                                mt-[21.5px] /* Pushes the card down inside the grid cell to leave room for the badge */
                                relative overflow-hidden
                                rounded-[16px] lg:rounded-[24px]
                                flex flex-col items-center text-center
                                bg-gradient-to-b from-white to-white/15
                                backdrop-blur-[5px] lg:backdrop-blur-[3px]
                                border border-solid border-white lg:border-2
                                shadow-[0_4px_20px_0_rgba(133,193,116,0.15)] lg:shadow-[0_14px_276px_0_rgba(174,205,183,0.6)]
                                w-full ${step.cardHeight}
                            `}
                        >
                            {/* Text content - Kept the extra padding so the text clears the badge */}
                            <div className="px-4 pt-[38px] pb-[140px] lg:pt-[34px] lg:pb-[150px] lg:px-[17px] text-center w-full">
                                <h3 className="font-poppins font-semibold text-[#212121] text-[21px] lg:text-[22px] leading-[1.2] m-0 mb-[6px]">
                                    {step.title}
                                </h3>
                                <p className="font-poppins font-normal text-[14px] lg:text-[15px] leading-[1.4] m-0 text-black/70 tracking-[-0.3px]">
                                    {step.description}
                                </p>
                            </div>

                            {/* Card image */}
                            <div className={`absolute bottom-0 left-0 right-0 flex justify-center items-end h-[130px] lg:h-[140px] ${step.imageContainerHeight}`}>
                                <img
                                    src={step.image}
                                    alt={step.imageAlt}
                                    className="max-h-full w-auto object-contain"
                                />
                            </div>
                        </div>

                        {/* 2. THE BADGE GOES SECOND (So it renders in full color on top of the blur) */}
                        <div className="
                            col-start-1 row-start-1 z-50
                            justify-self-center self-start
                            w-[43px] h-[43px] rounded-full
                            bg-primary-button-gradient
                            flex items-center justify-center
                            shadow-[0_4px_4px_0_rgba(0,0,0,0.04)]
                            [backface-visibility:hidden]
                        ">
                            <span className="font-poppins font-medium text-white text-[22px] leading-none">
                                {step.number}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Mobile carousel navigation */}
            <div className="flex lg:hidden items-center justify-between mt-4 px-[15px] max-w-[1100px] mx-auto">
                <button
                    type="button"
                    onClick={goToPrev}
                    aria-label="Previous step"
                    className="w-[56px] h-[56px] rounded-full border border-solid border-white
                        bg-[#f5f6f7] text-[#1f2129]
                        shadow-[0_-12px_16px_0_rgba(255,255,255,0.82),0_12px_18px_0_rgba(206,208,216,0.7)]
                        active:scale-95 transition flex items-center justify-center"
                >
                    <svg viewBox="0 0 18 16" className="w-[16px] h-[14px]" fill="currentColor" aria-hidden="true" style={{ transform: "scaleX(-1)" }}>
                        <path d="M10.293.293a1 1 0 0 1 1.414 0l6.586 6.586a1.5 1.5 0 0 1 0 2.121l-6.586 6.586a1 1 0 0 1-1.414-1.414L15.586 9H1a1 1 0 1 1 0-2h14.586L10.293 1.707a1 1 0 0 1 0-1.414z"/>
                    </svg>
                </button>

                <div className="flex items-center gap-[19px]">
                    {steps.map((step, index) => (
                        <span
                            key={step.number}
                            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                                index === activeIndex ? "bg-[#72b33e]" : "bg-white"
                            }`}
                        />
                    ))}
                </div>

                <button
                    type="button"
                    onClick={goToNext}
                    aria-label="Next step"
                    className="w-[56px] h-[56px] rounded-full border border-solid border-white
                        bg-[#f5f6f7] text-[#1f2129]
                        shadow-[0_-12px_16px_0_rgba(255,255,255,0.82),0_12px_18px_0_rgba(206,208,216,0.7)]
                        active:scale-95 transition flex items-center justify-center"
                >
                    <svg viewBox="0 0 18 16" className="w-[16px] h-[14px]" fill="currentColor" aria-hidden="true">
                        <path d="M10.293.293a1 1 0 0 1 1.414 0l6.586 6.586a1.5 1.5 0 0 1 0 2.121l-6.586 6.586a1 1 0 0 1-1.414-1.414L15.586 9H1a1 1 0 1 1 0-2h14.586L10.293 1.707a1 1 0 0 1 0-1.414z"/>
                    </svg>
                </button>
            </div>
        </section>
    );
}
